
import { useEffect, useRef, useState, lazy, Suspense } from "react"
import {
  ArrowRightIcon,
  PhoneIcon,
  ShieldCheckIcon,
  TagIcon,
} from "lucide-react"

import { LogoStrip, TestimonialCarousel } from "../Components/Offerspage/ClientProof"
import { HeroStage } from "../Components/Offerspage/HeroStage"
import { JobChooser } from "../Components/Offerspage/JobChooser"
import { PricingPlans } from "../Components/Offerspage/PricingPlans"
import { SectionHeading } from "../Components/Diwalipage/SectionHeading"
import { UseCaseCarousel } from "../Components/Offerspage/UseCaseCarousel"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../Components/Diwalipage/Accordion"

import { buttonVariants } from "../Components/Diwalipage/Button"
import { site } from "../config/site"
import { getAttribution } from "../lib/attribution"
import { isExpired } from "../lib/onbbitsOffer"
import { trackEvent } from "../lib/tracking"
import { usePageView } from "../lib/usePageView"
import { cn } from "../lib/utils"

import {
  clientLogos,
  demo,
  entry,
  faq,
  finalCta,
  h1ByAdGroup,
  hero,
  integrations,
  JOB_IDS,
  jobByAdGroup,
  jobs,
  logoStripLabel,
  LP_ID,
  noneJob,
  OFFER_END,
  offerBar,
  pricing,
  quotes,
  useCaseCopy,
  casesForJob,
  casesMixed,
  industries,
} from "../OfferContent";
import { SiteFooter } from "../Components/Diwalipage/SiteFooter"
import { SiteHeader } from "../Components/Offerspage/SiteHeader"
import OfferBar from "../Components/Offerspage/OfferBar"
import { daysLeft } from "../OfferDate";

// Integration logos
const integrationIcons = {
  ERPNext: './Images/erp-next-logo.svg',
  "Google Contacts": './Images/google-contact-logo.svg',
  "Google Sheets": './Images/google-sheets-logo.svg',
  WooCommerce: './Images/woocommerce-logo.svg',
  Razorpay: './Images/razorpay-logo.svg',
  WordPress: './Images/wordpress-logo.svg',
  Pabbly: './Images/pabbly-logo.svg',
  "API Integration": './Images/api-integration-logo.svg',
}

// Lead form loads separately
const LeadForm = lazy(() =>
  import("../Components/Offerspage/LeadForm").then((m) => ({ default: m.LeadForm }))
)

const DEMO = "#demo"
const CHOOSE = "#choose"

const container = "mx-auto w-full max-w-[1140px] px-4 sm:px-6"

// Get ad group
function adGroupKey() {
  const fromUrl = new URLSearchParams(window.location.search).get("utm_content")

  return (
    fromUrl ??
    getAttribution().utm_content ??
    "ag1"
  ).toLowerCase()
}

// Initial job selection
function initialJob() {
  const q = new URLSearchParams(window.location.search).get("job")

  if (q && JOB_IDS.includes(q)) {
    return { job: q, source: "url" }
  }

  const ag = jobByAdGroup[adGroupKey()]

  return ag
    ? { job: ag, source: "adgroup" }
    : { job: "none", source: null }
}
// Call link
function CallLine({ location, dark }) {
  return (
    <a
      href={site.phoneHref}
      onClick={() => trackEvent("call_click", { location })}
      className={cn(
        "inline-flex min-h-11 items-center gap-1.5 font-medium underline underline-offset-2",
        dark
          ? "text-on-dark hover:text-white"
          : "text-ink-strong hover:text-brand-text"
      )}
    >
      <PhoneIcon className="size-4" aria-hidden="true" />
      or call {site.phoneDisplay}
    </a>
  )
}

// Primary CTA
function PrimaryCta({
  offerActive,
  location,
  dark,
  align = "start",
}) {
  return (
    <div
      className={cn(
        "flex w-full flex-col gap-2",
        align === "center"
          ? "items-center"
          : "items-stretch lg:items-start"
      )}
    >
      <a
        href={DEMO}
        onClick={() =>
          trackEvent("cta_click", { lp_id: LP_ID, location })
        }
        className={buttonVariants({
          size: "lg",
          className: "w-full shadow-glow sm:w-auto",
        })}
      >
        {hero.cta}
        <ArrowRightIcon data-icon="inline-end" aria-hidden="true" />
      </a>

      {offerActive && (
        <p
          className={cn(
            "inline-flex w-fit items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold",
            align === "center" && "self-center",
            dark
              ? "bg-brand/20 text-brand-bright"
              : "bg-campaign-tint text-campaign-text"
          )}
        >
          <TagIcon className="size-3.5 shrink-0" aria-hidden="true" />
          {hero.offerHighlight}
        </p>
      )}
    </div>
  )
}

export default function Offers() {
  usePageView()

  const offerActive = !isExpired(OFFER_END)
  const left = daysLeft(OFFER_END);
const daysLabel = left <= 1 ? "Last day" : `${left} days left`;

  const [planInterest, setPlanInterest] = useState(null)
const [init] = useState(initialJob)
const [job, setJob] = useState(init.job)

  const [h1Lead, h1Muted] =
    h1ByAdGroup[adGroupKey()] ?? h1ByAdGroup.ag1

  const sent = useRef(false)
 const stageRef = useRef(null)

  // Reveal hero stage on mobile after selecting a job
  const revealStage = () => {
    const el = stageRef.current

    if (!el || window.matchMedia("(min-width: 1024px)").matches) return

    const r = el.getBoundingClientRect()

    if (r.top < window.innerHeight * 0.6) return

    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches

    requestAnimationFrame(() =>
      el.scrollIntoView({
        behavior: reduce ? "auto" : "smooth",
        block: "start",
      })
    )
  }

  // Track initial job selection
  useEffect(() => {
    if (sent.current || !init.source) return

    sent.current = true

    trackEvent("job_select", {
      lp_id: LP_ID,
      job: init.job,
      source: init.source,
    })
  }, [init])

  const pick = (id, source) => {
    setJob(id)

    trackEvent("job_select", {
      lp_id: LP_ID,
      job: id,
      source,
    })
  }

  const j = job === "none" ? null : jobs[job]
  const view = j ?? noneJob

  const stageCards = j
    ? industries.slice(0, 2).map((i) => ({
        industry: i.industry,
        widgets: i.cases[j.id].widgets,
      }))
    : casesMixed(JOB_IDS)
        .slice(0, 2)
        .map((u) => ({
          industry: u.industry,
          tag: jobs[u.job].short,
          widgets: u.widgets,
        }))

  const carouselItems = j
    ? casesForJob(j.id)
    : casesMixed(JOB_IDS).map((u) => ({
        ...u,
        job: jobs[u.job].short,
      }))

  const entryJobs = JOB_IDS
    .filter((id) => id !== job)
    .map((id) => jobs[id])

  const faqItems = view.faqOrder
  .concat(
    Object.keys(faq.items)
      .map(Number)
      .filter((n) => !view.faqOrder.includes(n))
  )
  .filter((n) => offerActive || n !== 10)
  .map((n) => {
    const [q, a] = faq.items[n]

    return [
      q,
      !offerActive && n === 7 ? faq.costEnded : a,
    ]
  })

  useEffect(() => {
        document.title =
          "Onbbits Offers: Save 25% on WhatsApp Automation Plans";
    
        const metaDesc = document.querySelector("meta[name='description']");
        if (metaDesc) {
          metaDesc.setAttribute(
            "content",
            "Save 25% on eligible ONBBITS yearly plans for WhatsApp automation. Compare plan features, explore pricing and book a free demo to get started today."
          );
        }
    
         const canonical = document.querySelector("link[rel='canonical']");
      if (canonical) {
        canonical.setAttribute("href", "https://onbbits.io/onbbits-offer");
      }
    
      const ogTitle = document.querySelector("meta[property='og:title']");
      if (ogTitle) {
        ogTitle.setAttribute(
          "content",
          "Onbbits Offers: Save 25% on WhatsApp Automation Plans"
        );
      }
    
        const ogDesc = document.querySelector("meta[property='og:description']");
      if (ogDesc) {
        ogDesc.setAttribute(
          "content",
          "Save 25% on eligible ONBBITS yearly plans for WhatsApp automation. Compare plan features, explore pricing and book a free demo to get started today."
        );
      }
    
      const ogUrl = document.querySelector("meta[property='og:url']");
      if (ogUrl) {
        ogUrl.setAttribute("content", "https://onbbits.io/onbbits-offer");
      }
    
        const ogImage = document.querySelector("meta[property='og:image']");
      if (ogImage) {
        ogImage.setAttribute(
          "content",
          "https://onbbits.io/Images/flow-builder.webp"
        );
      }
    
        const twitterTitle = document.querySelector("meta[name='twitter:title']");
      if (twitterTitle) {
        twitterTitle.setAttribute(
          "content",
          "Onbbits Offers: Save 25% on WhatsApp Automation Plans"
        );
      }
    
        const twitterDesc = document.querySelector(
        "meta[name='twitter:description']"
      );
      if (twitterDesc) {
        twitterDesc.setAttribute(
          "content",
          "Save 25% on eligible ONBBITS yearly plans for WhatsApp automation. Compare plan features, explore pricing and book a free demo to get started today."
        );
      }
    
        const twitterImage = document.querySelector("meta[name='twitter:image']");
      if (twitterImage) {
        twitterImage.setAttribute(
          "content",
          "https://onbbits.io/Images/flow-builder.webp"
        );
      }
      }, [])
  
const [testimonialJump, setTestimonialJump] = useState(0);

const quoteIndex = j?.quote ?? 0;
const quote = quotes.items[quoteIndex];
  return (
    <>
   <SiteHeader
  ctaLabel="Book a Free Demo"
  ctaHref="#demo"
  banner={
    offerActive ? (
      <OfferBar
        {...offerBar}
        daysLabel={daysLabel}
        href="#demo"
      />
    ) : null
  }
/>
    
  <main>
        {/* 1. Hero */}
        <section
          id="choose"
          aria-labelledby="hero-title"
          className={cn(
            "scroll-mt-28 bg-surface-cream pb-12 md:pb-16",
            offerActive
              ? "pt-32 md:pt-40"
              : "pt-24 md:pt-32"
          )}
        >
          <div
            className={cn(
              container,
              "grid items-start gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12"
            )}
          >
            <div className="flex flex-col items-start gap-4">
              <p className="text-xs font-bold tracking-[0.08em] text-brand-text uppercase sm:text-sm">
                {hero.eyebrow}
              </p>

              <h1
                id="hero-title"
                className="text-[30px] leading-[1.15] font-medium tracking-[-0.8px] text-balance sm:text-[38px] lg:text-[44px] lg:tracking-[-1.2px]"
              >
                {h1Lead}{" "}
                <span className="text-gradient-brand">
                  {h1Muted}
                </span>
              </h1>

              <p className="max-w-xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
                {hero.sub}
              </p>

              <div className="mt-1 flex w-full flex-col items-start gap-0.5">
                <JobChooser
                  variant="tile"
                  label={hero.chooserLabel}
                  value={j?.id ?? null}
                  onChange={(id) => {
                    pick(id, "tile")
                    revealStage()
                  }}
                  options={JOB_IDS.map((id) => ({
                    id,
                    label: jobs[id].label,
                    short: jobs[id].short,
                    helper: jobs[id].helper,
                  }))}
                />

                <button
                  type="button"
                  onClick={() => pick("none", "reset")}
                  aria-hidden={job === "none" || undefined}
                  tabIndex={job === "none" ? -1 : undefined}
                  className={cn(
                    "min-h-10 text-sm font-medium text-brand-text underline underline-offset-2",
                    job === "none" && "invisible"
                  )}
                >
                  {hero.showAll}
                </button>
              </div>

              <div className="flex w-full flex-col gap-1">
                <PrimaryCta
                  offerActive={offerActive}
                  location="hero"
                />

                <p className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                  <span>{view.underCta}</span>
                  <CallLine location="hero" />
                </p>
              </div>

              {offerActive && (
                <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                  <span>
                    Starter{" "}
                    <span className="text-muted-foreground line-through decoration-1">
                      <span className="sr-only">was </span>
                      ₹1,199
                    </span>{" "}
                    <span className="text-lg font-semibold">
                      ₹899
                    </span>
                    /mo
                  </span>

                  <span>
                    Pro{" "}
                    <span className="text-muted-foreground line-through decoration-1">
                      <span className="sr-only">was </span>
                      ₹2,999
                    </span>{" "}
                    <span className="text-lg font-semibold">
                      ₹2,249
                    </span>
                    /mo
                  </span>

                  <span className="text-muted-foreground">
                    billed yearly, + 18% GST ·{" "}
                    <a
                      href="https://onbbits.io/terms-conditions"
                      className="font-medium text-brand-text underline underline-offset-2"
                    >
                      {hero.termsLink}
                    </a>
                  </span>
                </p>
              )}

              <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-strong sm:text-sm">
                {hero.credentials.map((c) => (
                  <li
                    key={c}
                    className="flex items-center gap-1.5"
                  >
                    <ShieldCheckIcon
                      className="size-4 text-brand-text"
                      aria-hidden="true"
                    />
                    {c}
                  </li>
                ))}
              </ul>
            </div>

           <HeroStage
  ref={stageRef}
  label={hero.stageLabel}
  caption={j?.label}
  cards={stageCards}
  steps={view.flow.map((n) => n.title)}
  quote={{
    metric: quote.metric,
    label: quote.label,
    by: `${quote.name}, ${quote.role.split(", ").pop()}`,
    href: "#proof",
    onClick: () => setTestimonialJump((prev) => prev + 1),
  }}
  stateKey={job}
/>
          </div>
        </section>

        {/* 2. Client logos */}
        <LogoStrip
          label={logoStripLabel}
          logos={clientLogos}
        />

        {/* 3. Use cases */}
        <section
          id="answer"
          className="scroll-mt-28 py-16 md:py-20"
        >
          <div className={cn(container, "flex flex-col gap-10")}>
            <SectionHeading
              accentStyle="muted"
              eyebrow={useCaseCopy.eyebrow}
              lead={view.useHead[0]}
              accent={view.useHead[1]}
            />

            <UseCaseCarousel
              key={job}
              items={carouselItems}
              label={useCaseCopy.eyebrow}
              prevLabel={useCaseCopy.prev}
              nextLabel={useCaseCopy.next}
              swipeHint={useCaseCopy.swipe}
            />

            <div className="grid items-center gap-6 rounded-2xl border border-border bg-background p-6 shadow-card sm:p-8 lg:grid-cols-[1.3fr_0.7fr]">
              {j ? (
                <div className="flex flex-col gap-3">
                  <h3 className="text-[22px] leading-snug font-semibold">
                    {j.featureTitle}
                  </h3>

                  <p className="text-base leading-[1.8] font-light text-ink-strong">
                    {j.featureText}
                  </p>

                  <p className="w-fit rounded-md border border-border bg-surface-soft px-2.5 py-1 text-xs font-semibold text-brand-text">
                    {j.tag}
                  </p>
                </div>
              ) : (
                <ul className="grid gap-3 sm:grid-cols-2">
                  {JOB_IDS.map((id) => (
                    <li
                      key={id}
                      className="flex flex-col gap-1"
                    >
                      <button
                        type="button"
                        onClick={() => pick(id, "entry")}
                        className="w-fit text-left text-base font-semibold underline-offset-2 hover:underline"
                      >
                        {jobs[id].featureTitle}
                      </button>

                      <span className="text-xs font-semibold text-brand-text">
                        {jobs[id].tag}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <PrimaryCta
                offerActive={offerActive}
                location="use_cases"
              />
            </div>
          </div>
        </section>

        {/* 4. Testimonials */}
       <section
  id="proof"
  className="scroll-mt-28 bg-surface-soft py-16 md:py-20"
>
  <div className={cn(container, "mx-auto max-w-[900px]")}>
   <TestimonialCarousel
  items={quotes.items}
  lead={view.quoteLead}
  footnote={quotes.footnote}
  targetIndex={quoteIndex}
  jumpTrigger={testimonialJump}
/>
  </div>
</section>

        {/* 5. Features and integrations */}
        <section className="bg-mint py-16 md:py-20">
          <div className={cn(container, "flex flex-col gap-10")}>
            <SectionHeading
              accentStyle="muted"
              eyebrow={entry.eyebrow}
              lead={entry.h2Lead}
              accent={entry.h2Accent}
            />

            <ul
              className={cn(
                "grid gap-4",
                entryJobs.length === 4
                  ? "sm:grid-cols-2 lg:grid-cols-4"
                  : "md:grid-cols-3"
              )}
            >
              {entryJobs.map((e) => (
                <li
                  key={e.id}
                  className="flex flex-col gap-3 rounded-xl border border-border bg-background p-5 shadow-card"
                >
                  <h3 className="text-lg font-semibold">
                    {e.label}
                  </h3>

                  <p className="text-sm leading-relaxed text-ink-strong">
                    {e.helper}.
                  </p>

                  <p className="text-xs font-semibold text-brand-text">
                    {e.tag}
                  </p>

                  <a
                    href="#answer"
                    onClick={() => pick(e.id, "entry")}
                    className="mt-auto inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-foreground underline underline-offset-2"
                  >
                    {entry.link}
                    <ArrowRightIcon
                      className="size-4"
                      aria-hidden="true"
                    />
                  </a>
                </li>
              ))}
            </ul>

            {/* Integrations */}
            <div className="relative left-1/2 w-screen -translate-x-1/2 px-4">
           <div className="mx-auto flex w-full max-w-8xl flex-col items-center gap-3 text-center px-4">
              <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {integrations.label}
              </p>

              <ul className="flex flex-wrap justify-center gap-2">
                {integrations.chips.map((c) => (
                  <li
                    key={c}
                    className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-3 py-1.5 text-sm font-medium"
                  >
                    {integrationIcons[c] && (
                      <img
                        src={integrationIcons[c]}
                        alt=""
                        aria-hidden="true"
                        className="size-8 shrink-0 object-contain -ml-2"
                      />
                    )}
                    {c}
                  </li>
                ))}
              </ul>

              <p className="max-w-2xl text-sm text-muted-foreground">
                {integrations.line}
              </p>
            </div>
          </div>
          </div>
        </section>

        {/* 6. Pricing */}
        <section
          id="pricing"
          className="scroll-mt-28 py-16 md:py-24"
        >
          <div className={cn(container, "flex flex-col gap-10")}>
            <SectionHeading
              eyebrow={
                offerActive
                  ? pricing.eyebrow
                  : pricing.endedEyebrow
              }
              lead={
                offerActive
                  ? pricing.h2Lead
                  : pricing.endedH2Lead
              }
              accent={
                offerActive
                  ? pricing.h2Accent
                  : pricing.endedH2Accent
              }
              sub={
                offerActive
                  ? pricing.sub
                  : pricing.endedSub
              }
            />

            <PricingPlans
              plans={pricing.plans}
              copy={pricing}
              offerActive={offerActive}
              formHref={DEMO}
              highlight={j?.pricingMatch}
              highlightNote={j?.pricingLine}
              onToggle={(billing) =>
                trackEvent("pricing_toggle", {
                  lp_id: LP_ID,
                  billing,
                })
              }
              onPlanClick={(plan, billing) => {
                setPlanInterest(plan)

                trackEvent("plan_click", {
                  lp_id: LP_ID,
                  plan,
                  billing,
                })
              }}
            />

            <div className="flex flex-col items-center gap-2 text-center">
              <p className="text-sm text-ink-strong">
                {pricing.below}
              </p>
            </div>
          </div>
        </section>

        {/* 7. Demo form */}
        <section
          id="demo"
          className="scroll-mt-28 bg-surface-cream py-16 md:py-24"
        >
          <div
            className={cn(
              container,
              "grid items-start gap-10 lg:grid-cols-[0.9fr_1.1fr]"
            )}
          >
            <div className="flex flex-col gap-6 lg:sticky lg:top-32">
              <SectionHeading
                align="left"
                accentStyle="muted"
                eyebrow={
                  offerActive
                    ? demo.eyebrow
                    : demo.endedEyebrow
                }
                lead={
                  offerActive
                    ? demo.h2Lead
                    : demo.endedH2Lead
                }
                accent={
                  offerActive
                    ? view.demoAccent
                    : demo.endedH2Accent
                }
                sub={
                  offerActive
                    ? demo.body
                    : demo.endedBody
                }
              />

              <div className="flex flex-col gap-3">
                <p className="text-sm font-semibold">
                  {demo.stepsTitle}
                </p>

                <ol className="flex flex-col gap-3">
                  {demo.steps.map(([title, text], i) => (
                    <li
                      key={title}
                      className="flex gap-4 rounded-lg border border-border bg-background p-4 shadow-card"
                    >
                      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-charcoal text-sm font-semibold text-white">
                        {i + 1}
                      </span>

                      <p className="text-sm leading-relaxed">
                        <span className="font-semibold">
                          {title}
                        </span>{" "}
                        {offerActive
                          ? text
                          : i === 0
                            ? demo.endedStep1
                            : i === 2
                              ? demo.endedStep3
                              : text}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>

              <p className="text-sm text-muted-foreground">
                {demo.preferCall}{" "}
                <a
                  href={site.phoneHref}
                  onClick={() =>
                    trackEvent("call_click", {
                      location: "demo",
                    })
                  }
                  className="font-medium text-brand-text underline underline-offset-2"
                >
                  {site.phoneDisplay}
                </a>
              </p>
            </div>

            <Suspense
              fallback={
                <div
                  className="min-h-[640px] rounded-lg border border-border bg-background shadow-card-float"
                  aria-busy="true"
                />
              }
            >
              <LeadForm
                offerActive={offerActive}
                planInterest={planInterest}
                changeHref={CHOOSE}
                goal={{
                  id: job,
                  label: j?.label ?? null,
                  waLine: j?.waLine,
                  confirmLine: j?.confirmLine,
                }}
              />
            </Suspense>
          </div>
        </section>

        {/* 8. FAQ */}
        <section className="py-16 md:py-20">
          <div
            className={cn(
              container,
              "grid gap-10 lg:grid-cols-[0.8fr_1.2fr]"
            )}
          >
            <div className="flex flex-col items-start gap-5 lg:sticky lg:top-32 lg:self-start">
              <SectionHeading
                align="left"
                eyebrow={faq.eyebrow}
                lead={faq.h2}
              />

              <p className="text-sm leading-relaxed text-muted-foreground">
                Still unsure? Call us on{" "}
                <a
                  href={site.phoneHref}
                  onClick={() =>
                    trackEvent("call_click", {
                      location: "faq",
                    })
                  }
                  className="font-medium text-brand-text underline underline-offset-2"
                >
                  {site.phoneDisplay}
                </a>{" "}
                or write to{" "}
                <a
                  href={`mailto:${site.email}`}
                  className="font-medium text-brand-text underline underline-offset-2"
                >
                  {site.email}
                </a>
                .
              </p>

              <a
                href={DEMO}
                className={buttonVariants({ size: "lg" })}
              >
                {hero.cta}
              </a>
            </div>

            <Accordion
              key={job}
              defaultValue={[0]}
              className="flex flex-col gap-3"
            >
              {faqItems.map(([q, a], i) => (
                <AccordionItem
                  key={q}
                  value={i}
                  className="rounded-lg border border-border bg-background px-5 shadow-card-raised"
                >
                  <AccordionTrigger className="py-5 text-base font-medium hover:no-underline">
                    {q}
                  </AccordionTrigger>

                  <AccordionContent className="text-sm leading-relaxed font-light text-ink-strong">
                    {a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* 9. Final CTA */}
        <section className="px-3 pb-16 sm:px-4 md:pb-20">
          <div className="bg-dark-glow-tr mx-auto flex max-w-[1140px] flex-col items-center gap-5 rounded-lg px-6 py-14 text-center text-on-dark shadow-card-float sm:py-16">
            <h2 className="max-w-2xl text-[26px] leading-tight font-normal text-balance sm:text-[34px]">
              {view.finalLead}{" "}
              <span className="font-semibold text-brand-bright">
                {j ? finalCta.accent : noneJob.finalAccent}
              </span>
            </h2>

            <PrimaryCta
              offerActive={offerActive}
              location="final_cta"
              align="center"
              dark
            />

            <p className="flex flex-wrap items-center justify-center gap-x-2 text-sm text-on-dark-muted">
              <ShieldCheckIcon
                className="size-4 text-brand-bright"
                aria-hidden="true"
              />

              {(offerActive
                ? finalCta.under
                : finalCta.underEnded
              ).join(" · ")}{" "}
              · <CallLine location="final_cta" dark />
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </>
  )
}
